<template>
    <Head title="Create Notification & Routing Policy" />

    <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create New Notification & Routing Policy</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define who gets notified and routed when work orders matching specific criteria are created.</p>
                        </div>
                        <InfoButton title="Creating Notification & Routing Policies" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                Notification & Routing Policies automate alerting the right people when work orders are created. By setting
                                matching criteria (work order type, service category, priority), you control who receives email
                                and in-app notifications. Recipients can be individual users (specific people) or roles (everyone
                                with that role). More specific policies override general ones, allowing fine-grained control.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Policy Name:</strong> Descriptive name like "Urgent HVAC Notifications" or "All
                                  Corrective Work"<br>
                                • <strong>Matching Criteria:</strong> Leave fields blank for broader matching - blank = all
                                  values<br>
                                • <strong>Work Order Type:</strong> Corrective, Preventive, Predictive, Inspection, Calibration,
                                  or Emergency<br>
                                • <strong>Service Category:</strong> Optional - narrows to specific category like Electrical or
                                  HVAC<br>
                                • <strong>Service:</strong> Optional - even more specific than category, targets individual
                                  services<br>
                                • <strong>Priority:</strong> Low, Normal, High, or Urgent - critical for escalation<br>
                                • <strong>Recipient Type:</strong> Choose "Individual Users" for specific people or "Roles" for
                                  anyone with that role<br>
                                • <strong>Recipients:</strong> Select at least one user or role - these people receive
                                  notifications<br>
                                • <strong>Active:</strong> Check to enable notifications immediately
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Roles are dynamic</strong> - Role-based notifications automatically include new users
                                  added to that role<br>
                                • <strong>Specificity hierarchy:</strong> Category + Priority > Priority alone - most specific
                                  policy wins<br>
                                • <strong>Start broad</strong> - Create general "All Urgent" policy first, then add specific
                                  overrides<br>
                                • <strong>Test your policy</strong> - After creation, create a matching test work order to verify
                                  notifications<br>
                                • <strong>Avoid overlaps</strong> - Too many policies = notification fatigue. Keep it simple<br>
                                • <strong>Use descriptive names</strong> - Clear names help you identify policies quickly later
                            </p>
                        </InfoButton>
                    </div>
                </div>

                <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                    <!-- Name Field -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.name }"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.name }}
                        </p>
                    </div>

                    <!-- Description Field -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.description }"
                        ></textarea>
                        <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.description }}
                        </p>
                    </div>

                    <!-- Criteria Section -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Matching Criteria</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Leave criteria blank to apply to all work orders of that type. More specific policies will take precedence. Service requires a category selection.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <!-- Work Order Type -->
                            <div>
                                <label for="work_order_type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Work Order Type
                                </label>
                                <select
                                    id="work_order_type"
                                    v-model="form.work_order_type"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.work_order_type }"
                                >
                                    <option value="">All Types</option>
                                    <option value="corrective">Corrective</option>
                                    <option value="preventive">Preventive</option>
                                    <option value="predictive">Predictive</option>
                                    <option value="inspection">Inspection</option>
                                    <option value="calibration">Calibration</option>
                                    <option value="emergency">Emergency</option>
                                </select>
                                <p v-if="form.errors.work_order_type" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.work_order_type }}
                                </p>
                            </div>

                            <!-- Service Category -->
                            <div>
                                <label for="service_category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Service Category
                                </label>
                                <select
                                    id="service_category_id"
                                    v-model="form.service_category_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.service_category_id }"
                                >
                                    <option value="">All Categories</option>
                                    <option v-for="category in serviceCategories" :key="category.id" :value="category.id">
                                        {{ category.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.service_category_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.service_category_id }}
                                </p>
                            </div>

                            <!-- Service -->
                            <div>
                                <label for="service_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Service
                                </label>
                                <select
                                    id="service_id"
                                    v-model="form.service_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.service_id }"
                                    :disabled="!form.service_category_id"
                                >
                                    <option value="">All Services</option>
                                    <option v-for="service in filteredServices" :key="service.id" :value="service.id">
                                        {{ service.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.service_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.service_id }}
                                </p>
                                <p v-if="!form.service_category_id" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Select a category first to choose a specific service
                                </p>
                            </div>

                            <!-- Priority -->
                            <div>
                                <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Priority
                                </label>
                                <select
                                    id="priority"
                                    v-model="form.priority"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.priority }"
                                >
                                    <option value="">All Priorities</option>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                                <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.priority }}
                                </p>
                            </div>

                            <!-- Location -->
                            <div>
                                <label for="location_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Location
                                </label>
                                <select
                                    id="location_id"
                                    v-model="form.location_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.location_id }"
                                >
                                    <option value="">All Locations</option>
                                    <option v-for="location in locations" :key="location.id" :value="location.id">
                                        {{ location.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.location_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.location_id }}
                                </p>
                            </div>

                            <!-- Asset -->
                            <div v-if="$page.props.subscriptionFeatures.asset_module">
                                <label for="asset_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Asset
                                </label>
                                <select
                                    id="asset_id"
                                    v-model="form.asset_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300': form.errors.asset_id }"
                                    :disabled="!form.location_id"
                                >
                                    <option value="">All Assets</option>
                                    <option v-for="asset in filteredAssets" :key="asset.id" :value="asset.id">
                                        {{ asset.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.asset_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.asset_id }}
                                </p>
                                <p v-if="!form.location_id" class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Select a location first to choose a specific asset
                                </p>
                            </div>
                        </div>

                        <!-- Duplicate Criteria Error -->
                        <div v-if="form.errors.criteria" class="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500 rounded-md">
                            <p class="text-sm text-red-600 dark:text-red-400">
                                {{ form.errors.criteria }}
                            </p>
                        </div>
                    </div>

                    <!-- Recipients Section -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Notification Recipients <span class="text-red-500 dark:text-red-400">*</span></h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Select who should be notified when matching work orders are created.</p>

                        <!-- Recipient Type Selection -->
                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recipient Type</label>
                            <div class="flex items-center space-x-6">
                                <div class="flex items-center">
                                    <input
                                        id="recipient_type_users"
                                        v-model="form.recipient_type"
                                        type="radio"
                                        value="users"
                                        class="h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label for="recipient_type_users" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Individual Users
                                    </label>
                                </div>
                                <div class="flex items-center">
                                    <input
                                        id="recipient_type_roles"
                                        v-model="form.recipient_type"
                                        type="radio"
                                        value="roles"
                                        class="h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                    <label for="recipient_type_roles" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Roles
                                    </label>
                                </div>
                            </div>
                            <p v-if="form.errors.recipient_type" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                {{ form.errors.recipient_type }}
                            </p>
                        </div>

                        <!-- User Selection (if recipient_type is users) -->
                        <div v-if="form.recipient_type === 'users'">
                            <label for="users" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Users <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-64 overflow-y-auto bg-white dark:bg-gray-700">
                                <div v-for="user in users" :key="user.id" class="flex items-center py-2">
                                    <input
                                        :id="`user-${user.id}`"
                                        v-model="form.recipient_ids"
                                        type="checkbox"
                                        :value="user.id"
                                        class="h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 rounded"
                                    />
                                    <label :for="`user-${user.id}`" class="ml-3 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                        {{ user.name }} <span class="text-gray-500 dark:text-gray-400">({{ user.email }})</span>
                                    </label>
                                </div>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Selected: {{ form.recipient_ids.length }} user(s)</p>
                            <p v-if="form.errors.recipient_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                {{ form.errors.recipient_ids }}
                            </p>
                        </div>

                        <!-- Role Selection (if recipient_type is roles) -->
                        <div v-if="form.recipient_type === 'roles'">
                            <label for="roles" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Roles <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-64 overflow-y-auto bg-white dark:bg-gray-700">
                                <div v-for="role in roles" :key="role.id" class="flex items-start py-2">
                                    <input
                                        :id="`role-${role.id}`"
                                        v-model="form.recipient_ids"
                                        type="checkbox"
                                        :value="role.id"
                                        class="h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 rounded mt-0.5"
                                    />
                                    <label :for="`role-${role.id}`" class="ml-3 block text-sm cursor-pointer">
                                        <span class="text-gray-900 dark:text-white font-medium">{{ role.name }}</span>
                                        <span v-if="role.description" class="block text-gray-500 dark:text-gray-400 text-xs">{{ role.description }}</span>
                                    </label>
                                </div>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Selected: {{ form.recipient_ids.length }} role(s)</p>
                            <p v-if="form.errors.recipient_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                {{ form.errors.recipient_ids }}
                            </p>
                        </div>
                    </div>

                    <!-- Work Order Routing Section -->
                    <div class="bg-gray-50 dark:bg-gray-700 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Work Order Routing</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Route work orders to specific Maintenance Support Managers. If no users are selected, all Maintenance Support Managers can access the work order.
                        </p>
                        <div class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-500 rounded-md p-3 mb-4">
                            <p class="text-sm text-yellow-700 dark:text-yellow-300">
                                <strong>Note:</strong> This is only for routing. If you want these users to also receive notifications, you must add them to the Notification Recipients section above.
                            </p>
                        </div>

                        <!-- Routing User Selection -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Maintenance Support Managers
                            </label>
                            <div class="border border-gray-300 dark:border-gray-600 rounded-md p-3 max-h-64 overflow-y-auto bg-white dark:bg-gray-700">
                                <div v-if="maintenanceSupportManagerUsers && maintenanceSupportManagerUsers.length > 0">
                                    <div v-for="user in maintenanceSupportManagerUsers" :key="user.id" class="flex items-center py-2">
                                        <input
                                            :id="`routing-user-${user.id}`"
                                            v-model="form.routing_ids"
                                            type="checkbox"
                                            :value="user.id"
                                            class="h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 rounded"
                                        />
                                        <label :for="`routing-user-${user.id}`" class="ml-3 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                            {{ user.name }} <span class="text-gray-500 dark:text-gray-400">({{ user.email }})</span>
                                        </label>
                                    </div>
                                </div>
                                <div v-else class="text-sm text-gray-500 dark:text-gray-400">
                                    No users with Maintenance Support Manager role found.
                                </div>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Selected: {{ form.routing_ids.length }} user(s)</p>
                            <p v-if="form.errors.routing_ids" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                {{ form.errors.routing_ids }}
                            </p>
                        </div>
                    </div>

                    <!-- Is Active Checkbox -->
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                            <p class="text-gray-500 dark:text-gray-400">Enable this policy to send notifications immediately.</p>
                        </div>
                    </div>

                    <!-- Info Box -->
                    <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>How it works:</strong> When a work order is created, the system finds the most specific matching policy. Recipients receive both email and in-app notifications. Users in selected roles are automatically determined at notification time (not when the policy is created).
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Link
                            :href="route('settings.maintenance.notification-policies.index')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md shadow-sm hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="form.processing">Creating...</span>
                            <span v-else>Create Notification Policy</span>
                        </button>
                    </div>
                </form>
            </div>
    </div>
</template>

<script setup>
import { watch, computed } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    serviceCategories: Array,
    services: Array,
    users: Array,
    roles: Array,
    locations: Array,
    assets: Array,
    maintenanceSupportManagerUsers: Array,
});

const toast = useToast();

const form = useSecureForm({
    name: '',
    description: '',
    work_order_type: '',
    service_category_id: '',
    service_id: '',
    location_id: '',
    asset_id: '',
    priority: '',
    recipient_type: 'users',
    recipient_ids: [],
    routing_ids: [],
    is_active: true,
});

// Filter services based on selected category
const filteredServices = computed(() => {
    if (!form.service_category_id) {
        return props.services || [];
    }
    return (props.services || []).filter(service => service.service_category_id === form.service_category_id);
});

// Watch service_category_id changes to clear service_id
watch(() => form.service_category_id, () => {
    form.service_id = '';
});

// Watch location_id changes to clear asset_id
watch(() => form.location_id, () => {
    form.asset_id = '';
});

// Watch recipient_type changes to clear recipient_ids
watch(() => form.recipient_type, () => {
    form.recipient_ids = [];
});

// Filter assets based on selected location
const filteredAssets = computed(() => {
    if (!form.location_id) {
        return props.assets || [];
    }
    return (props.assets || []).filter(asset => asset.location_id === form.location_id);
});

const submit = () => {
    form.post(route('settings.maintenance.notification-policies.store'), {
        onError: () => {
            toast.error('Failed to create notification policy. Please check the form for errors.');
        }
    });
};
</script>
